import { Button, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { _ } from '/@/locale';

/**
 * Small "type one number" modal, used for both the achievement percentage and the field
 * surface. Validation runs as you type so the confirm button reflects the current value,
 * matching the error state in the design.
 */
function NumberModal({
    open,
    onClose,
    onApply,
    title,
    description,
    value,
    suffix,
    min,
    max,
    rangeError,
}: {
    open: boolean;
    onClose: () => void;
    onApply: (value: number) => void;
    title: string;
    description: string;
    value: number | undefined;
    suffix?: string;
    min: number;
    max: number;
    rangeError: string;
}) {
    const [draft, setDraft] = useState('');

    useEffect(() => {
        if (open) setDraft(value !== undefined ? String(value) : '');
    }, [open, value]);

    const parsed = Number(draft);
    const isValid = draft !== '' && Number.isFinite(parsed) && parsed >= min && parsed <= max;
    const showError = draft !== '' && !isValid;

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            title={title}
            maxWidth='xs'
            actions={
                <Stack direction='row' spacing={2} justifyContent='flex-end' width='100%'>
                    <Button variant='text' color='primary' onClick={onClose}>
                        {_('Cancelar')}
                    </Button>
                    <Button
                        variant='contained'
                        color='primary'
                        disabled={!isValid}
                        onClick={() => onApply(parsed)}
                        data-testid='number-modal-apply'
                    >
                        {_('Aplicar')}
                    </Button>
                </Stack>
            }
        >
            <Stack spacing={2} p={2}>
                <Typography variant='body2' color='primary'>
                    {description}
                </Typography>
                <TextField
                    value={draft}
                    onChange={(event) => setDraft(event.target.value.replace(/[^0-9.]/g, ''))}
                    error={showError}
                    helperText={showError ? rangeError : undefined}
                    InputProps={{ endAdornment: suffix }}
                    inputMode='decimal'
                    autoFocus
                    fullWidth
                />
            </Stack>
        </ModalV2>
    );
}

export default NumberModal;
